import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UnifiedAuditRow } from '@/api/console';
import { ActionCell, ActorCell } from '@/pages/Console/Audit/auditCells';
import type { AccountDetail } from '@/api/accounts';
import { isPendingFirstLogin, type KeycloakAccountRow } from '../types';
import { AccountProfilePlacementsTable } from './AccountProfilePlacementsTable';
import styles from './accountProfile.module.css';

interface Props {
    detail: AccountDetail;
    lastLoginAvailable: boolean;
    recentAudit: UnifiedAuditRow[];
    onViewAllAudit: () => void;
}

function formatSource(source: AccountDetail['source'], t: (key: string) => string): string {
    if (source === 'console') return t('console.accounts.profile.sourceConsole');
    return t('console.accounts.profile.sourceUnknown');
}

function formatCreated(created: number | null | undefined): string {
    if (!created) return '—';
    return new Date(created).toLocaleDateString('vi-VN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatLastLogin(
    lastLogin: number | null | undefined,
    available: boolean,
    t: (key: string) => string
): string {
    if (!available) return t('console.accounts.table.lastLoginUnavailable');
    if (!lastLogin) return t('console.accounts.table.lastLoginNotRecorded');
    return new Date(lastLogin).toLocaleString('vi-VN');
}

/**
 * EAA-1820 (frame 72, tab Tổng quan): two-column layout — identity + permissions
 * table on the left, "why access" and recent activity on the 330px aside.
 */
export function AccountProfileOverviewTab({
    detail,
    lastLoginAvailable,
    recentAudit,
    onViewAllAudit,
}: Props) {
    const { t } = useTranslation();

    return (
        <div className={styles.overview}>
            <div className={styles.overviewMain}>
                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>{t('console.accounts.profile.identityTitle')}</h2>
                    <dl className={styles.dl}>
                        <dt className={styles.dlTerm}>{t('console.accounts.profile.sourceLabel')}</dt>
                        <dd className={styles.dlDef}>
                            {formatSource(detail.source, t)} · {formatCreated(detail.created)}
                        </dd>
                        <dt className={styles.dlTerm}>{t('console.accounts.profile.lastLoginLabel')}</dt>
                        <dd className={styles.dlDef}>{formatLastLogin(detail.last_login, lastLoginAvailable, t)}</dd>
                        <dt className={styles.dlTerm}>{t('console.accounts.detailDrawer.statusLabel')}</dt>
                        <dd className={styles.dlDef}>
                            {!detail.enabled
                                ? t('console.accounts.table.statusLocked')
                                : isPendingFirstLogin(detail as KeycloakAccountRow)
                                  ? t('console.accounts.table.statusPendingFirstLogin')
                                  : t('console.accounts.table.statusActive')}
                        </dd>
                    </dl>
                </section>

                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>
                        {t('console.accounts.profile.placementsTableTitle')}
                    </h2>
                    <AccountProfilePlacementsTable detail={detail} />
                    {detail.placements.length > 0 && (
                        <p className={`portal-admin-hint ${styles.permHint}`}>
                            {t('console.accounts.profile.permissionsHint')}{' '}
                            <Link to="/console/role-map">{t('console.accounts.profile.roleMapLink')}</Link>
                        </p>
                    )}
                </section>
            </div>

            <aside className={styles.overviewAside}>
                {detail.app_access.length > 0 && (
                    <section className={`${styles.card} ${styles.cardMuted}`}>
                        <h2 className={styles.cardTitle}>{t('console.accounts.profile.whyAccessTitle')}</h2>
                        <p className={styles.cardHint}>{t('console.accounts.profile.whyAccessHint')}</p>
                        {detail.app_access.slice(0, 2).map((app) => (
                            <p key={app.application} className={styles.whyLine}>
                                {t('console.accounts.profile.whyAccessSample', {
                                    app: app.application_name,
                                    roles: app.roles.join(', '),
                                })}
                            </p>
                        ))}
                    </section>
                )}

                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>{t('console.accounts.profile.recentActivityTitle')}</h2>
                    {recentAudit.length === 0 ? (
                        <p className="portal-admin-hint">{t('console.accounts.profile.recentActivityEmpty')}</p>
                    ) : (
                        <ul className={styles.activity}>
                            {recentAudit.map((row) => (
                                <li key={row.id} className={styles.activityItem}>
                                    <span className={styles.activityDot} aria-hidden="true" />
                                    <span className={styles.activityBody}>
                                        <span>
                                            <ActorCell row={row} />{' '}
                                            <ActionCell action={row.action} />
                                        </span>
                                        <span className={styles.activityMeta}>
                                            {new Date(row.created_at).toLocaleString('vi-VN')}
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button type="button" className={styles.linkBtn} onClick={onViewAllAudit}>
                        {t('console.accounts.profile.viewFullAudit')}
                    </button>
                </section>
            </aside>
        </div>
    );
}

export default AccountProfileOverviewTab;
